<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
const {outputDateFromDb} = useCrudConverters();
import CompetitionQuestionsPrompt from "@/components/competitions/CompetitionQuestionsPrompt.vue";
import CompetitionPdfDialog from '~/components/competitions/CompetitionPdfDialog.vue';
import {canExportCompetitionPdf, EXPORT_PDF_LABEL} from '~/composables/useCompetitionPdf';
import FormatExplainerDialog from '~/components/formats/FormatExplainerDialog.vue';
import {computed, ref,onMounted, onBeforeUnmount} from "vue";
import OutstandingFeeBanner from "~/components/payments/OutstandingFeeBanner.vue";
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import ClubChip from "~/components/clubs/ClubChip.vue";
import TournamentBracket from "~/components/tournaments/TournamentBracket.vue";
import TournamentChampionBanner from "~/components/tournaments/TournamentChampionBanner.vue";
import JoinRequestsCard from "~/components/join/JoinRequestsCard.vue";
import ActionMenuItem from "~/components/navigation/ActionMenuItem.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import FixtureMatrixKey from "~/components/leagues/FixtureMatrixKey.vue";
import InformationDialog from "~/components/InformationDialog.vue";
import NotPublishedNotice from "~/components/NotPublishedNotice.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import JoinRequestPending from "~/components/join/JoinRequestPending.vue";
import {createError} from "nuxt/app";
import HandicapCalculatorButton from '~/components/handicaps/HandicapCalculatorButton.vue';
import {useHandicapSettingsFor} from '~/composables/useHandicapSettingsFor';

definePageMeta({
  name: 'TournamentDetail',
  path: '/tournaments/:tournamentId',
});

const {
  item: tournament,
  getItem: getTournament,
  hasItemErrors,
  itemPending: tournamentLoading
} = useCrudApi("api/v2/tournaments");

// Handicap calculator in the action row: the tournament's settings (null = handicaps off) + every entrant
const {settings: handicapSettings} = useHandicapSettingsFor(
    computed(() => tournament.value?.id ? {tournamentId: tournament.value.id} : null),
    computed(() => tournament.value?.handicapsEnabled === true));
const handicapEntries = computed(() => (tournament.value?.teams ?? [])
    .map((tt) => ({teamId: tt.teamId ?? tt.team?.id ?? tt.id, name: tt.team?.name ?? tt.name ?? '', value: tt.handicap ?? null})));

const route = useRoute();
const router = useRouter();
const tournamentId = route.params.tournamentId;

await getTournament(tournamentId);

if (hasItemErrors.value) {
  throw createError({
    statusCode: 404,
    message: 'Tournament not found. Has it been deleted?'
  });
}

const {setMeta} = useHeadService();
setMeta(
    tournament.value?.club?.name + ' / ' + tournament.value?.name + ' on Tap Leagues',
    tournament.value?.club?.name + ' / ' + tournament.value?.name + ' tournament on Tap Leagues. View tournament brackets, matches and results.'
);

// The tournament has been deleted (archived) - show the deleted notice instead of content.
const tournamentArchived = computed(() => !!tournament.value?.archivedAt);
const tournamentPrivate = computed(() => !!tournament.value?.privateToViewer);
usePrivatePage(tournamentPrivate);

const {isAuthenticated, user, isAdminOfClub, isMemberOfClub} = useAuthService();
const {addListener, removeLocalListenersWithEvent} = useListenerService();

// Export PDF (group standings + knockout rounds): club admins and super
// admins only (owner decision, 7 Oct 2026).
const pdfOpen = ref(false);
const canExportPdf = computed(() => !!tournament.value && canExportCompetitionPdf(user.value, tournament.value.clubId));

const formatName = computed(() => {
  return tournament.value.format === 1 ? 'Knockout' : 'Groups then Knockout';
});

const knockoutTeamIds = computed(() => {
  if (!tournament.value?.stages) return [];

  const teamIds = new Set();
  const knockoutStages = tournament.value.stages.filter(s => s.type === 2 || s.type === 3);

  knockoutStages.forEach(stage => {
    if (stage.teams && Array.isArray(stage.teams)) {
      stage.teams.forEach(team => {
        if (team.id) {
          teamIds.add(team.id);
        }
      });
    }
  });

  return Array.from(teamIds);
});

const isInTournament = computed(() => {
  if (!tournament.value?.teams || !user.value?.playerId) return false;
  return tournament.value.teams.some(tt => tt.team?.players?.some(p => p.id === user.value.playerId));
});

// Filter join link through the same visibility rules as the other competition pages
const visibleJoinLink = computed(() => {
  const raw = tournament.value?.visibleInviteLink;
  if (!raw) return null;
  if (isAdminOfClub(tournament.value.clubId)) return raw;

  // Non-admins require: visible (0), active, and scope-allowed
  const scopeAllowed = raw.inviteScope !== 1 || isMemberOfClub(tournament.value.clubId);
  return raw.inviteEntityPageSetting !== 1 && raw.isActive && scopeAllowed
      ? raw : null;
});

// Tournaments can only be joined by invite link before the draw has been generated
const drawGenerated = computed(() => (tournament.value?.stages?.length ?? 0) > 0);

// The user's own pending join request for this tournament (from the auth user object).
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest} =
    useJoinRequestStatus('tournament', computed(() => tournament.value?.id));

// CHAT FUNCTIONALITY
const {entityChatLoading, entityChatErrors, createOrOpenEntityChat} = useMessaging();

const canAccessChat = computed(() => {
  if (!isAuthenticated.value || !tournament.value) return false;
  return isInTournament.value || isAdminOfClub(tournament.value.clubId);
});

const handleChatClick = async () => {
  await createOrOpenEntityChat(
      {tournamentId: tournament.value.id, conversationId: tournament.value.conversationId},
      () => getTournament(tournamentId)
  );
};

const titleActions = computed(() => {
  const actions = tournament.value && isAdminOfClub(tournament.value.clubId) ? [{
    title: 'Manage tournament',
    icon: 'mdi-cog',
    click: () => router.push({
      name: 'ClubTournamentManage',
      params: {clubId: tournament.value.clubId, tournamentId: tournament.value.id}
    }),
    color: 'info',
  }] : [];

  return actions;
});

// Listen for fixture updates
onMounted(() => {
  addListener('fixtureUpdated', async (fixture) => {
    if (fixture != null && fixture.tournamentId === tournament.value.id) {
      await getTournament(tournamentId);
    }
  });
  // Approving/rejecting a join request updates accessApplicationCount.
  addListener('requestsUpdated', () => getTournament(tournamentId));
});

onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
  removeLocalListenersWithEvent('requestsUpdated');
});

</script>

<template>
  <base-page>
    <!-- Deleted (archived) tournament - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): identity only, then the notice where the content would be -->
    <v-container v-if="tournamentPrivate" class="large">
      <frontend-header :tournament="tournament"
          :club="tournament.club ?? (tournament.clubId ? {id: tournament.clubId, slug: tournament.clubId, name: tournament.clubName} : null)"
          :title="tournament.name"
          title-icon="mdi-trophy"
          title-icon-color="tournament"
      >
        <template #subTitle>
          <club-chip v-if="tournament.club" :club="tournament.club"></club-chip>
          <span v-else-if="tournament.clubName" class="tl-chip">{{ tournament.clubName }}</span>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <private-notice reason="competition" entity="tournament" :name="tournament.name" :club-name="tournament.clubName" :join-slug="tournament.visibleInviteLink?.slug"
                      :facts="competitionFacts('tournament', tournament, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
    </v-container>
    <v-container v-else-if="tournamentArchived" class="large">
      <archived-notice entity="tournament" :name="tournament.name"/>
    </v-container>
    <v-container v-else class="large">

      <outstanding-fee-banner v-if="isAuthenticated" :source-id="route.params.tournamentId"/>

      <!-- Page head: breadcrumbs + h1 + head-chips + admin action -->
      <frontend-header
          v-if="tournament"
          :tournament="tournament"
          :actions="titleActions"
          :action-menu="true"
          title-icon="mdi-trophy" title-icon-color="warning"
      >
        <template #title>
          <h1 class="mb-0">{{ tournament.name }}</h1>
          <div class="head-chips">
            <club-chip :club="tournament.club"/>
            <v-chip size="small" class="tl-chip format-chip">
              <v-icon icon="mdi-tournament" size="13" class="mr-1"/>
              {{ formatName }}
            </v-chip>
          </div>
        </template>
      </frontend-header>

      <!-- Unpublished (draft) tournaments aren't viewable publicly -->
      <not-published-notice v-if="tournament && !tournament.published" entity="tournament" class="my-4"/>

      <!-- Action strip (share / fixtures / stats / chat / join / information) -->
      <div v-if="tournament && tournament.published" class="tl-action-strip my-4">
        <!-- Export PDF (club admins and super admins only) -->
        <button v-if="canExportPdf" class="as" @click="pdfOpen = true">
          <v-icon size="17">mdi-file-pdf-box</v-icon>
          {{ EXPORT_PDF_LABEL }}
        </button>

        <!-- Share -->
        <short-link-share-wrapper
                    :join-reference-type="4" :join-reference-id="tournament.id"
                    :can-share-join="isAdminOfClub(tournament.clubId)">
          <template #default="{click, loading: shareLoading}">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17">mdi-share-variant</v-icon>
              Share
            </button>
          </template>
        </short-link-share-wrapper>

        <!-- View Fixtures -->
        <router-link
            class="as"
            :to="{name: 'FixtureSearch', query: {clubIds: [tournament.clubId], tournamentIds: [tournament.id]}}"
        >
          <v-icon size="17">mdi-scoreboard</v-icon>
          View Matches
        </router-link>

        <!-- Stats -->
        <router-link class="as" :to="{ name: 'TournamentStats', params: { tournamentId: tournament.id } }">
          <v-icon size="17">mdi-chart-line</v-icon>
          Stats
        </router-link>

        <!-- Key — matrix legend (only tournaments with a group stage show a matrix) -->
        <fixture-matrix-key v-if="tournament.format === 2"/>

        <!-- Chat -->
        <button
            v-if="canAccessChat"
            class="as"
            @click="handleChatClick"
            :disabled="entityChatLoading"
        >
          <v-icon size="17">mdi-chat</v-icon>
          {{ tournament.conversationId ? 'Join Chat' : 'Start Chat' }}
        </button>

        <!-- Handicap calculator (handicaps on) -->
        <handicap-calculator-button action :admin="isAdminOfClub(tournament.clubId)" :settings="handicapSettings" :entries="handicapEntries"
                                    :scoring-system-id="tournament?.scoringSystemId ?? null"
                                    :noun="(tournament?.playersPerTeam ?? 1) > 1 ? 'team' : 'player'"/>

        <!-- Pending request (already applied, pre-draw) -->
        <join-request-pending
            v-if="!isInTournament && !drawGenerated && hasPendingRequest"
            compact
            :entity-name="tournament.name"
            :cancelling="cancelPending"
            @cancel="cancelRequest"
        />

        <!-- Join Tournament (pre-draw, invite-only) -->
        <router-link
            v-else-if="!isInTournament && visibleJoinLink && !drawGenerated"
            class="as"
            :to="'/invite/' + visibleJoinLink.slug"
        >
          <v-icon size="17">mdi-account-plus</v-icon>
          Join Tournament
        </router-link>

        <!-- Information (admin-authored rich text) — only when content exists -->
        <format-explainer-dialog format="tournament" :admin="!!tournament && isAdminOfClub(tournament.clubId)" :payments="tournament?.entryFeeMinor != null"/>
        <competition-pdf-dialog v-if="canExportPdf" v-model="pdfOpen" format="tournament" :competition-id="tournament.id" :name="tournament.name"/>
        <information-dialog :html="tournament?.informationHtml" :title="`${tournament?.name} information`"/>
      </div>

      <!-- Admin invite CTA — join-link state at a glance, opens the join-links dialog -->
      <join-settings-card
          v-if="tournament && isAdminOfClub(tournament.clubId)"
          class="my-4"
          :reference-type="4"
          :reference-id="tournament.id"
          :club-id="tournament.clubId"
          :entity-name="tournament.name"
          show-tournament-warning
          @changed="getTournament(tournamentId)"
      />

      <!-- Pending join requests (admins only; count rides on the tournament response) -->
      <join-requests-card
          v-if="tournament && isAdminOfClub(tournament.clubId)"
          :count="tournament.accessApplicationCount ?? 0"
          :filters="{ tournamentIds: [tournament.id] }"
      />

      <!-- Champions banner + confetti once the final is played and fully approved -->
      <tournament-champion-banner v-if="tournament && tournament.published" :tournament="tournament"/>

      <!-- Tournament Bracket (owns tab switcher + all stage content) -->
      <div v-if="tournament && tournament.published" class="tournament-bracket-wrapper">
        <tournament-bracket
            :tournament="tournament"
            :knockout-team-ids="knockoutTeamIds"
            :is-admin="isAdminOfClub(tournament.clubId)"
        />

        <!-- Loading overlay -->
        <div v-if="tournamentLoading" class="tournament-loading-overlay">
          <v-progress-circular indeterminate color="primary" size="48"/>
          <div class="text-caption mt-3">Loading tournament…</div>
        </div>
      </div>

    </v-container>
    <competition-questions-prompt competition-type="tournament" :competition-id="tournament?.id"/>
  </base-page>
</template>

<style scoped lang="scss">
/* ---- Page head chips row (below h1) ---- */
.head-chips {
  display: flex;
  gap: 6px;
  margin-top: 9px;
  flex-wrap: wrap;
  align-items: center;
}

.format-chip {
  font-size: 11px;
  font-weight: 600;
}

/* .tl-action-strip + .as / .vsep are global primitives (settings.scss) */

/* ---- Bracket wrapper + loading overlay ---- */
.tournament-bracket-wrapper {
  position: relative;
  min-height: 200px;
}

.tournament-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.75);
  border-radius: 14px;
  color: rgb(var(--v-theme-charcoal));
  font-weight: 600;
  z-index: 10;
}
</style>
